import React from "react";
import { AlertBox, AlertBoxDismissButton, Box, Column, GridContainer, Row } from "@vp/swan";

import "./Notification.scss";

import { Trans } from "react-i18next";
import { useTranslation } from "../../useTranslation";
import {
  Notification as ContextNotification,
  NotificationState
} from "../../NotificationContext";
import { useAuthenticationContext } from "../AuthenticationContext";

import { getLastAttemptInfo, pushDataToSegment } from "../../segmentData";
import { ACCOUNT_CREATION_PAGE, MULTIPLE_ACCOUNT_FOUND_PAGE, PASSWORD_RESET_PAGE, SIGN_IN_PAGE } from "../../constant";

interface NotificationProps {
  notification: NotificationState;
  clearNotification: Function;
}

class CustomerAlert {
  private _dataTestId?: string;

  private _message!: string;

  private _messageKey?: string;

  private _sourcePage?: string;

  constructor (
    public skin: "positive" | "warning" | "error" | "legal-warning" = "error"
  ) {}

  public get dataTestId (): string | undefined {
    return this._dataTestId;
  }

  public set dataTestId (value: string | undefined) {
    this._dataTestId = value;
  }

  public get message (): string {
    return this._message;
  }

  public set message (value: string) {
    this._message = value;
  }

  public get messageKey (): string | undefined {
    return this._messageKey;
  }

  public set messageKey (value: string | undefined) {
    this._messageKey = value;
  }

  public get sourcePage (): string | undefined {
    return this._sourcePage;
  }

  public set sourcePage (value: string | undefined) {
    this._sourcePage = value;
  }
}

export const NotificationControl: React.FunctionComponent<NotificationProps> = (
  props: NotificationProps
) => {
  const [t, i18n] = useTranslation();
  const en = i18n.getFixedT("en"); // data sent to segment should always be in 'en'
  const { notification, clearNotification } = props;
  const { canShowCallbackError, disableVisualizationOfCallbackError } = useAuthenticationContext();

  const createMessage = (
    namespace: string,
    notification: NotificationState
  ): { message: string; messageKey: string | undefined } => {
    let message, messageKey;

    if (notification.errorCode) {
      message = t(`${namespace}:serverErrors.${notification.errorCode}`);
      messageKey = `${namespace}:serverErrors.${notification.errorCode}`;
    }
    if (
      message === undefined ||
      message === `serverErrors.${notification.errorCode}`
    ) {
      message = t(`${namespace}:formErrorMessage`);
      messageKey = `${namespace}:formErrorMessage`;
    }

    return { message, messageKey };
  };

  const createLoginFailedMessage = (
    errorCode?: string
  ): { message: string; messageKey: string | undefined } => {
    let messageKey;

    switch (errorCode) {
      case "social-connection-already-exists":
        messageKey = "signUp:serverErrors.social-connection-already-exists";
        break;
      case "google-oauth2_no_email":
        messageKey = "social:google.errors.no_email";
        break;
      case "facebook_no_email":
        messageKey = "social:facebook.errors.no_email";
        break;
      case "login_from_restricted_region":
        messageKey = "signIn:serverErrors.login_from_restricted_region";
        break;
      case "invalid_user_password_for_non_sva":
        messageKey = "signIn:serverErrors.invalid_user_password";
        break;
      case "user_banned":
        messageKey = "signIn:serverErrors.user_banned";
        break;
      case "force_password_reset_on_vcs_login":
        messageKey = "signIn:serverErrors.force_password_reset_on_vcs_login";
        break;
      case "different_email_while_stepped_up":
        messageKey = "signIn:serverErrors.different_email_while_stepped_up";
        break;
      default:
        messageKey = "callbackError";
        break;
    }

    return { message: t(messageKey), messageKey };
  };

  const createCustomerAlert = (
    notification: NotificationState
  ): CustomerAlert | undefined => {
    const alert = new CustomerAlert();
    let messageContent;

    switch (notification.notification) {
      case ContextNotification.ResetEmailSent:
        alert.skin = "positive";
        alert.dataTestId = "resetNotification";
        alert.message = t("reset:resetEmailSent");
        alert.messageKey = "reset:resetEmailSent";
        alert.sourcePage = PASSWORD_RESET_PAGE;
        break;
      case ContextNotification.ResetEmailFailed:
        alert.dataTestId = "resetFailed";
        messageContent = createMessage("reset", notification);
        alert.message = messageContent.message;
        alert.messageKey = messageContent.messageKey;
        alert.sourcePage = PASSWORD_RESET_PAGE;
        break;
      case ContextNotification.LoginFailed:
        alert.dataTestId = "sigInFailed";
        messageContent = createMessage("signIn", notification);
        alert.message = messageContent.message;
        alert.messageKey = messageContent.messageKey;
        if (notification.otherDetails?.isFirstLogin && notification.errorCode === "invalid_user_password") {
          alert.message = t("signIn:serverErrors.invalid_user_password_is_first_login");
        }
        alert.sourcePage = SIGN_IN_PAGE;
        break;
      case ContextNotification.SignUpFailed:
        alert.dataTestId = "sigUpFailed";
        messageContent = createMessage("signUp", notification);
        alert.message = messageContent.message;
        alert.messageKey = messageContent.messageKey;
        alert.sourcePage = ACCOUNT_CREATION_PAGE;
        break;
      case ContextNotification.InvalidFormSubmitted:
        alert.dataTestId = "invalidFormSubmitted";
        alert.message = t("invalidFormSubmitted");
        alert.messageKey = "invalidFormSubmitted";
        alert.sourcePage = notification.otherDetails?.pageName;
        break;
      case ContextNotification.LoginFailedCallbackError:
        alert.dataTestId = "callbackError";
        messageContent = createLoginFailedMessage(notification.errorCode);
        alert.message = messageContent.message;
        alert.messageKey = messageContent.messageKey;
        alert.sourcePage = SIGN_IN_PAGE;
        break;
      case ContextNotification.UnificationEmailSent:
        alert.skin = "positive";
        alert.dataTestId = "unificationEmailSent";
        alert.message = t("app:multipleAccountMessage.resendEmailSuccess");
        alert.messageKey = "app:reset.resendEmailSuccess";
        alert.sourcePage = MULTIPLE_ACCOUNT_FOUND_PAGE;
        break;
      case ContextNotification.UnificationEmailFailed:
        alert.dataTestId = "unificationEmailFailed";
        alert.message = t("app:multipleAccountMessage.resendEmailFailure");
        alert.messageKey = "app:multipleAccountMessage.resendEmailFailure";
        alert.sourcePage = MULTIPLE_ACCOUNT_FOUND_PAGE;
        break;
      default:
        return undefined;
    }

    return alert;
  };

  const showNotification = () => {
    const alert = createCustomerAlert(notification);

    if (alert !== undefined) {
      if (alert.dataTestId && alert.messageKey && !canShowCallbackError) {
        const lastAttemptInfo = JSON.parse(getLastAttemptInfo());
        pushDataToSegment(alert.dataTestId, false, {
          pageName: alert.sourcePage,
          label: lastAttemptInfo?.label,
          errorMessage: notification.errorCode === "invalid_captcha" ? (notification.otherDetails?.email !== "" ? `invalid_captcha : ${notification.otherDetails?.email}` : "invalid_captcha") : en(alert.messageKey),
          eventDetail: en(alert.messageKey)
        });
      }

      if (canShowCallbackError) {
        disableVisualizationOfCallbackError();
      }

      const getAlertBox = () => {
        return (
            <AlertBox
              className="padding-in-errorBox"
              skin={alert.skin}
              data-testid={alert.dataTestId}
              toast
              style={{ width: "100%" }}
              onRequestDismiss={() => {
                clearNotification();
              }}
            >
                {getNotificationMessage(alert)}
              <AlertBoxDismissButton visuallyHiddenLabel={t("dismiss")} />
            </AlertBox>
        );
      };

      return (
        <>
          <GridContainer>
            <Box mb="between-subsections">
              <Row>
                <Column
                  span={12}
                  offset={0}
                  spanSm={8}
                  offsetSm={2}
                  spanMd={6}
                  offsetMd={3}
                  spanLg={4}
                  offsetLg={4}
                  spanXl={4}
                  offsetXl={4}
                >
                  {getAlertBox()}
                </Column>
              </Row>
              </Box>
            </GridContainer>
        </>
      );
    }

    return <></>;
  };

  const getNotificationMessage = (alert: CustomerAlert) => {
    let finalMessage = <div className="text-size-6 strong" dangerouslySetInnerHTML={{ __html: alert.message }} />;
    if (notification.notification === ContextNotification.LoginFailedCallbackError && notification.errorCode === "different_email_while_stepped_up") {
      finalMessage = <Trans i18nKey="signIn:serverErrors.different_email_while_stepped_up" tOptions={notification.otherDetails}></Trans>;
    } else if (notification.notification === ContextNotification.ResetEmailSent) {
      finalMessage = <> {alert.message} <a href="#" onClick={resetPasswordResent}>{ t("reset:resendEmail")}</a> </>;
    } else if (notification.notification === ContextNotification.SignUpFailed && notification.errorCode === "user_exists") {
      finalMessage = <Trans i18nKey="signUp:serverErrors.user_exists" tOptions={notification.otherDetails?.options}>
      {[
        <a
          data-testid="signUp:serverErrors.user_exists"
          key="user_exists"
          href="#"
          onClick={switchToSignIn}
        >
          user_exists
        </a>
      ]}
    </Trans>;
    } else if (notification.otherDetails?.isFirstLogin && notification.errorCode === "invalid_user_password") {
      finalMessage = <Trans i18nKey="signIn:serverErrors.invalid_user_password_is_first_login" tOptions={notification.otherDetails?.options}>
      {[
        <a
          data-testid="signIn:serverErrors.invalid_user_password_is_first_login"
          key="invalid_user_password_is_first_login"
          href="#"
          onClick={switchToResetPassword}
        >
          user_exists
        </a>
      ]}
      </Trans>;
    }
    return finalMessage;
  };

  const resetPasswordResent = () => {
    notification.otherDetails.auth0Context.resetPassword(notification.otherDetails.email, () => { });
  };

  const switchToSignIn = () => {
    notification.otherDetails?.switchedToSignIn();
  };

  const switchToResetPassword = () => {
    notification.otherDetails?.switchToResetPassword();
  };

  return showNotification();
};

const Notification: React.FunctionComponent<NotificationProps> = React.memo(
  NotificationControl
);
export default Notification;
